import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export const TermsOfServicePage: React.FC = () => {
  return (
    <DocsLayout
      title='Terms of Service'
      description='Terms and conditions for using the Vehix platform'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Legal', path: '/docs/legal' },
        { label: 'Terms of Service' }
      ]}
    >
      <div className='max-w-[1000px]'>
        <p className='text-sm text-muted-foreground italic mb-8'>Last Updated: January 28, 2026</p>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>1. Agreement to Terms</h2>
          <p className='text-sm leading-6'>
            By accessing or using Vehix, you agree to be bound by these Terms of Service and all
            applicable laws and regulations. If you do not agree with any of these terms, you are
            prohibited from using this platform.
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>2. Description of Service</h2>
          <p className='text-sm leading-6 mb-4'>
            Vehix is a web-based platform provided by Kebi AI LLC that helps automotive dealerships
            manage vehicle safety recalls. The platform provides:
          </p>
          <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
            <li>Geographic visualization of recalls</li>
            <li>AI-powered routing and technician assignment</li>
            <li>Real-time tracking of recall completion</li>
            <li>Multi-tenant dealership management</li>
            <li>Campaign configuration and prioritization</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>3. User Accounts</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.1 Account Creation</h3>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for maintaining account security</li>
              <li>You must be authorized by your organization to use Vehix</li>
              <li>Accounts require administrator approval before activation</li>
            </ul>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>3.2 Account Security</h3>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>Maintain the confidentiality of your login credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>You are responsible for all activities under your account</li>
            </ul>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>4. Acceptable Use</h2>
          <p className='text-sm leading-6 mb-4'>
            You agree to use Vehix only for lawful purposes and in accordance with our{' '}
            <Link to='/docs/legal/acceptable-use' className='text-primary hover:underline'>
              Acceptable Use Policy
            </Link>
            . Prohibited activities include:
          </p>
          <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
            <li>Attempting to gain unauthorized access to the system</li>
            <li>Interfering with or disrupting the service</li>
            <li>Uploading malicious code or viruses</li>
            <li>Violating any applicable laws or regulations</li>
            <li>Using the service to harm others</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>5. Ownership and Your License to Use Vehix</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.1 Kebi AI LLC Owns Vehix</h3>
            <p className='text-sm leading-6 mb-4'>
              Vehix is owned entirely by Kebi AI LLC. This includes everything you see and use:
            </p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>The software and all its features</li>
              <li>The design, layout, and how it looks</li>
              <li>The technology that makes it work</li>
              <li>All improvements and updates we make</li>
              <li>Our name, logo, and branding</li>
            </ul>
            <p className='text-sm leading-6 mt-4'>
              These are protected by copyright, trademark, and other laws. Nothing in these terms
              gives you any ownership of Vehix or its parts.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.2 Your License (Permission to Use)</h3>
            <p className='text-sm leading-6 mb-4'>
              When you subscribe to Vehix, we give you permission (a &quot;license&quot;) to use it
              for your work. This license is:
            </p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>
                <strong>Limited:</strong> You can only use Vehix for managing recalls at your
                authorized dealership
              </li>
              <li>
                <strong>Non-exclusive:</strong> We can give the same permission to other customers
              </li>
              <li>
                <strong>Non-transferable:</strong> You cannot give or sell your access to anyone
                else
              </li>
              <li>
                <strong>Revocable:</strong> We can end your access if you break these rules
              </li>
            </ul>
            <p className='text-sm leading-6 mt-4'>
              <strong>Important:</strong> You are leasing access to Vehix. You do not own any part
              of it.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.3 What You Cannot Do</h3>
            <p className='text-sm leading-6 mb-4'>You agree that you will NOT:</p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>Copy, reproduce, or duplicate any part of Vehix</li>
              <li>
                Try to figure out how Vehix works (reverse engineer, decompile, or disassemble)
              </li>
              <li>Create anything based on Vehix or its features</li>
              <li>Remove any copyright notices or labels</li>
              <li>Share your login credentials with anyone</li>
              <li>Let unauthorized people access your account</li>
              <li>Take screenshots or recordings to share outside your organization</li>
              <li>Share information about how Vehix works with competitors</li>
              <li>Use Vehix to build a competing product</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.4 Confidentiality</h3>
            <p className='text-sm leading-6 mb-4'>
              Everything about Vehix is confidential. This includes:
            </p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>How the features work</li>
              <li>The design and user interface</li>
              <li>Any pricing or business information</li>
              <li>Documentation and training materials</li>
              <li>Any information we share with you about upcoming features</li>
            </ul>
            <p className='text-sm leading-6 mt-4'>
              You agree to keep this information private and only share it with authorized users at
              your organization who need it for their work.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.5 If You Break These Rules</h3>
            <p className='text-sm leading-6 mb-4'>
              If you violate any of these ownership or confidentiality rules:
            </p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>We may immediately terminate your access to Vehix</li>
              <li>You will not receive any refund</li>
              <li>We may take legal action to protect our property</li>
              <li>You may be required to pay damages</li>
            </ul>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>5.6 Your Data</h3>
            <p className='text-sm leading-6'>
              You own the data you put into Vehix (like customer information and recall records). By
              using Vehix, you give us permission to use your data only to provide the service to
              you. We will never sell your data or use it for other purposes. See our Privacy Policy
              for more details.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>6. Subscription and Fees</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>6.1 Paid Subscriptions</h3>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>Subscription fees are billed in advance on a recurring basis</li>
              <li>Fees are non-refundable except as required by law</li>
              <li>We may change fees with 30 days notice</li>
            </ul>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>6.2 Free Trial</h3>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>Free trials are available for new customers</li>
              <li>You will not be charged during the trial period</li>
              <li>Subscription begins when trial ends unless cancelled</li>
            </ul>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>7. Data Privacy and Security</h2>
          <p className='text-sm leading-6'>
            Your use of Vehix is also governed by our{' '}
            <Link to='/docs/legal/privacy' className='text-primary hover:underline'>
              Privacy Policy
            </Link>
            , which describes how we collect, use, and protect your information.
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>8. Smart Features and Suggestions</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>8.1 How Smart Features Help You</h3>
            <p className='text-sm leading-6 mb-4'>
              Vehix includes features that help you work faster by making suggestions:
            </p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>Finding efficient routes for technicians</li>
              <li>Matching recalls to technicians based on skills and location</li>
              <li>Organizing recalls by priority and location</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>8.2 You Make the Final Decision</h3>
            <p className='text-sm leading-6 mb-4'>
              <strong>Important:</strong> These suggestions are meant to help you, not replace your
              judgment.
            </p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>You are responsible for reviewing all suggestions before acting on them</li>
              <li>You can change or override any suggestion</li>
              <li>You should verify that assignments make sense for your situation</li>
            </ul>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>8.3 No Guarantee of Suggestions</h3>
            <p className='text-sm leading-6'>
              We do not guarantee that suggestions will be perfect or suitable for your specific
              situation. Suggestions are based on the information available and may not account for
              factors we do not know about. Always use your professional judgment.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>9. Our Commitment to You</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>9.1 Service Availability</h3>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>We aim to keep Vehix available 99.5% of the time each month</li>
              <li>We will tell you in advance about scheduled maintenance</li>
              <li>We work quickly to fix any unexpected issues</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>9.2 Support Response Times</h3>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>
                <strong>Urgent issues</strong> (Vehix is down): We respond within 4 business hours
              </li>
              <li>
                <strong>Important issues</strong> (feature not working): We respond within 8
                business hours
              </li>
              <li>
                <strong>General questions</strong>: We respond within 24 business hours
              </li>
            </ul>
            <p className='text-sm leading-6 mt-4'>
              Support hours: Monday through Friday, 8 AM to 6 PM Eastern Time
            </p>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>9.3 Things Beyond Our Control</h3>
            <p className='text-sm leading-6'>
              We are not responsible for service interruptions caused by internet outages, your
              equipment, third-party services, natural disasters, or other events outside our
              control.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>10. Termination</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>9.1 By You</h3>
            <p className='text-sm leading-6'>
              You may terminate your account at any time by contacting support.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>9.2 By Us</h3>
            <p className='text-sm leading-6 mb-4'>
              We may suspend or terminate your account if you:
            </p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>Violate these Terms of Service</li>
              <li>Fail to pay subscription fees</li>
              <li>Engage in fraudulent activity</li>
              <li>Pose a security risk to the platform</li>
            </ul>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>9.3 Effect of Termination</h3>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>Your access to the platform will be immediately revoked</li>
              <li>You may request an export of your data within 30 days</li>
              <li>We may delete your data after 90 days</li>
            </ul>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>11. Warranties and Disclaimers</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>10.1 Service Provided &quot;As Is&quot;</h3>
            <p className='text-sm leading-6'>
              Vehix is provided &quot;as is&quot; without warranties of any kind, either express or
              implied, including but not limited to implied warranties of merchantability, fitness
              for a particular purpose, or non-infringement.
            </p>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>10.2 No Guarantee of Results</h3>
            <p className='text-sm leading-6'>
              We do not guarantee that use of Vehix will result in any particular business outcome
              or recall completion rate.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>12. Limitation of Liability</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>12.1 What We Are Not Responsible For</h3>
            <p className='text-sm leading-6 mb-4'>
              Kebi AI LLC is not responsible for any losses that result from:
            </p>
            <ul className='text-sm leading-6 list-disc list-inside space-y-2'>
              <li>Decisions you make based on Vehix suggestions without verification</li>
              <li>Incorrect data you enter into the system</li>
              <li>Service interruptions beyond our control</li>
              <li>Actions of your employees or technicians</li>
              <li>Third-party services or data sources</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>12.2 Maximum Liability</h3>
            <p className='text-sm leading-6'>
              If we are found responsible for any damages, the most we will pay is the amount you
              paid us for Vehix in the 12 months before the issue occurred.
            </p>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>12.3 Types of Damages We Do Not Cover</h3>
            <p className='text-sm leading-6'>
              To the maximum extent allowed by law, we are not responsible for indirect, incidental,
              special, or consequential damages. This includes lost profits, lost business, lost
              data, or damage to your reputation.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>13. Indemnification</h2>
          <p className='text-sm leading-6'>
            You agree to protect Kebi AI LLC from any claims, damages, or costs (including legal
            fees) that result from your use of Vehix, your violation of these Terms, or your
            violation of any law or the rights of others.
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>14. Taking Your Data With You</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>14.1 Exporting Your Data</h3>
            <p className='text-sm leading-6'>
              You can request a copy of your data at any time. We will provide it in a common format
              (like CSV or JSON) within 30 days of your request.
            </p>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>14.2 After You Leave</h3>
            <p className='text-sm leading-6'>
              If you end your subscription, you have 30 days to request your data. After 90 days, we
              may delete your data from our systems. We keep some information longer if required by
              law.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>15. Changes to Terms</h2>
          <p className='text-sm leading-6 mb-4'>
            We reserve the right to modify these Terms at any time. We will notify you of material
            changes by:
          </p>
          <ul className='text-sm leading-6 list-disc list-inside space-y-2 mb-4'>
            <li>Posting the updated Terms on the platform</li>
            <li>Updating the &quot;Last Updated&quot; date</li>
            <li>Sending email notification for significant changes</li>
          </ul>
          <p className='text-sm leading-6'>
            Your continued use of Vehix after changes constitutes acceptance of the new Terms.
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>16. Governing Law</h2>
          <p className='text-sm leading-6'>
            These Terms shall be governed by and construed in accordance with the laws of the State
            of Florida, United States, without regard to its conflict of law provisions.
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>17. Dispute Resolution</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>15.1 Informal Resolution</h3>
            <p className='text-sm leading-6'>
              Before filing a claim, you agree to try to resolve the dispute informally by
              contacting us at <strong>legal@kebi.ai</strong>.
            </p>
          </div>

          <div>
            <h3 className='text-lg font-semibold mb-4'>15.2 Arbitration</h3>
            <p className='text-sm leading-6'>
              Any disputes that cannot be resolved informally shall be resolved through binding
              arbitration in accordance with the American Arbitration Association rules.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>18. Contact Information</h2>
          <div className='p-6 bg-muted/30 rounded-lg border border-border'>
            <p className='text-sm leading-6'>
              <strong>Kebi AI LLC</strong>
              <br />
              Email: <span className='text-primary'>legal@kebi.ai</span>
              <br />
              Support: <span className='text-primary'>support@kebi.ai</span>
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>19. Severability</h2>
          <p className='text-sm leading-6'>
            If any provision of these Terms is found to be unenforceable or invalid, that provision
            shall be limited or eliminated to the minimum extent necessary, and the remaining
            provisions shall remain in full force and effect.
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>20. Entire Agreement</h2>
          <p className='text-sm leading-6'>
            These Terms, together with our Privacy Policy and Acceptable Use Policy, constitute the
            entire agreement between you and Kebi AI LLC regarding Vehix.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
};
